import React, { useEffect, useRef, useState, useCallback } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { DecodeHintType, BarcodeFormat } from '@zxing/library';
import { X, Camera, RefreshCw, Zap, ZapOff } from 'lucide-react';

/**
 * Camera-based barcode scanner using ZXing — full-frame detection.
 * • No bounding box: entire video frame is scanned every ~200ms
 * • TRY_HARDER + multi-format hints (CODE128, EAN13, EAN8, UPC-A/E, ITF, QR)
 * • Continuous autofocus + high-res video (1920×1080 request)
 * • Torch toggle on supported devices
 * • Green flash overlay on successful detection
 *
 * Props: open, onClose, onDetected(code)
 */
const BarcodeScanner = ({ open, onClose, onDetected }) => {
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const controlsRef = useRef(null);
  const trackRef = useRef(null);
  const [error, setError] = useState('');
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [detected, setDetected] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);

  // Build ZXing hints for max speed/reliability
  const buildHints = () => {
    const hints = new Map();
    hints.set(DecodeHintType.TRY_HARDER, true);
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.CODE_128,
      BarcodeFormat.EAN_13,
      BarcodeFormat.EAN_8,
      BarcodeFormat.UPC_A,
      BarcodeFormat.UPC_E,
      BarcodeFormat.CODE_39,
      BarcodeFormat.CODE_93,
      BarcodeFormat.ITF,
      BarcodeFormat.QR_CODE,
      BarcodeFormat.DATA_MATRIX,
    ]);
    return hints;
  };

  useEffect(() => {
    if (!open) return;
    setError(''); setDetected(false); setTorchOn(false);

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError('Camera not supported in this browser. Try Chrome, Safari 11+, Firefox, or Edge.');
      return;
    }
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      setError('Camera requires HTTPS. Please access the site via https://');
      return;
    }

    // Time-slice hint: run decoder as often as ZXing allows (default ~500ms, we override to 200ms via timeBetweenScansMillis)
    const reader = new BrowserMultiFormatReader(buildHints(), { delayBetweenScanAttempts: 150 });
    readerRef.current = reader;

    // Explicit high-res permission prompt (needed on iOS Safari)
    navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: { ideal: 'environment' },
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        frameRate: { ideal: 30 },
      },
    })
      .then((stream) => {
        stream.getTracks().forEach(t => t.stop());
        return BrowserMultiFormatReader.listVideoInputDevices();
      })
      .then((vids) => {
        setDevices(vids);
        const back = vids.find(d => /back|rear|environment/i.test(d.label));
        setDeviceId(back?.deviceId || vids[0]?.deviceId || null);
      })
      .catch((e) => {
        if (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError') {
          setError('Camera permission denied. Click the camera icon in your browser address bar to allow, then retry.');
        } else if (e.name === 'NotFoundError' || e.name === 'DevicesNotFoundError') {
          setError('No camera found on this device.');
        } else if (e.name === 'NotReadableError' || e.name === 'TrackStartError') {
          setError('Camera is in use by another app. Close other apps and retry.');
        } else {
          setError(`Camera error: ${e.message || e.name}`);
        }
      });

    return () => {
      try { controlsRef.current?.stop(); } catch (_e) { /* ignore */ }
      controlsRef.current = null;
      trackRef.current = null;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !deviceId || !videoRef.current) return;
    let cancelled = false;
    setScanning(true); setError(''); setDetected(false);

    // Request high-res constrained stream directly so ZXing decodes full-frame HD
    const constraints = {
      video: {
        deviceId: { exact: deviceId },
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        frameRate: { ideal: 30 },
        advanced: [{ focusMode: 'continuous' }, { focusDistance: 0.1 }],
      },
    };

    readerRef.current
      .decodeFromConstraints(constraints, videoRef.current, (result, err, controls) => {
        if (cancelled) return;
        if (controls && !controlsRef.current) {
          controlsRef.current = controls;
          // Grab the active MediaStreamTrack for torch capability check
          const stream = videoRef.current?.srcObject;
          const track = stream?.getVideoTracks?.()[0];
          if (track) {
            trackRef.current = track;
            const caps = typeof track.getCapabilities === 'function' ? track.getCapabilities() : {};
            if (caps.torch) setTorchSupported(true);
            // Try continuous autofocus if supported
            const cs = typeof track.getConstraints === 'function' ? track.getConstraints() : {};
            if (!cs.focusMode && caps.focusMode?.includes('continuous')) {
              track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] }).catch(() => {});
            }
          }
        }
        if (result) {
          const text = result.getText();
          setDetected(true);
          try { controls?.stop(); } catch (_e) { /* ignore */ }
          setScanning(false);
          // Beep + flash briefly for feedback before dismissing
          try {
            const AC = window.AudioContext || window.webkitAudioContext;
            if (AC) {
              const ac = new AC();
              const o = ac.createOscillator(); const g = ac.createGain();
              o.type = 'sine'; o.frequency.value = 1200;
              o.connect(g); g.connect(ac.destination); g.gain.value = 0.15;
              o.start(); o.stop(ac.currentTime + 0.09);
            }
          } catch (_e) { /* ignore */ }
          setTimeout(() => {
            onDetected?.(text);
            onClose?.();
          }, 140);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(`Scan error: ${e.message || 'Camera unavailable'}`);
          setScanning(false);
        }
      });

    return () => {
      cancelled = true;
      try { controlsRef.current?.stop(); } catch (_e) { /* ignore */ }
      controlsRef.current = null;
      trackRef.current = null;
    };
  }, [deviceId, open, onClose, onDetected]);

  const toggleTorch = useCallback(async () => {
    const track = trackRef.current;
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn(!torchOn);
    } catch (_e) { /* torch not supported / failed */ }
  }, [torchOn]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black flex items-center justify-center p-2 sm:p-4" data-testid="barcode-scanner-modal">
      <div className="relative w-full max-w-2xl bg-[#0A0A08] text-white overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Camera size={18} className={detected ? 'text-emerald-400' : 'text-[#E29C9C]'} />
            <span className="text-xs uppercase tracking-[0.2em] font-semibold">
              {detected ? 'Detected!' : 'Full-frame Scanner'}
            </span>
          </div>
          <div className="flex items-center gap-3">
            {torchSupported && (
              <button
                onClick={toggleTorch}
                className={`p-1.5 border ${torchOn ? 'border-[#E29C9C] text-[#E29C9C]' : 'border-white/20 text-white/70'} hover:text-white`}
                data-testid="scanner-torch"
                title="Toggle flashlight"
              >
                {torchOn ? <Zap size={14} /> : <ZapOff size={14} />}
              </button>
            )}
            <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="scanner-close">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="relative bg-black aspect-video">
          <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" muted playsInline autoPlay />

          {/* Detection flash — full-screen green pulse */}
          {detected && (
            <div className="absolute inset-0 bg-emerald-400/25 border-4 border-emerald-400 animate-pulse pointer-events-none" />
          )}

          {/* Corner brackets only — no restrictive box; scanner reads entire frame */}
          {!detected && !error && (
            <>
              <div className="absolute top-4 left-4 w-6 h-6 border-l-2 border-t-2 border-[#E29C9C]" />
              <div className="absolute top-4 right-4 w-6 h-6 border-r-2 border-t-2 border-[#E29C9C]" />
              <div className="absolute bottom-4 left-4 w-6 h-6 border-l-2 border-b-2 border-[#E29C9C]" />
              <div className="absolute bottom-4 right-4 w-6 h-6 border-r-2 border-b-2 border-[#E29C9C]" />
              {/* Tiny scanning bar top */}
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#E29C9C] shadow-[0_0_16px_rgba(226,156,156,1)] animate-pulse" />
            </>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/85 px-6 text-center">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}
        </div>

        <div className="px-5 py-3 flex items-center justify-between text-[10px] uppercase tracking-[0.15em]">
          <span className={detected ? 'text-emerald-400' : 'text-white/60'}>
            {detected ? '✓ Ready' : scanning ? '◉ Scanning entire frame…' : '◌ Initialising…'}
          </span>
          {devices.length > 1 && (
            <select
              value={deviceId || ''}
              onChange={e => setDeviceId(e.target.value)}
              className="bg-transparent text-white/80 text-[10px] border border-white/20 px-2 py-1"
              data-testid="scanner-device-select"
            >
              {devices.map(d => <option key={d.deviceId} value={d.deviceId} className="text-black">{d.label || `Camera ${d.deviceId.slice(0, 6)}`}</option>)}
            </select>
          )}
          <button
            onClick={() => { setDeviceId(d => d); setError(''); }}
            className="text-[#E29C9C] hover:text-white flex items-center gap-1"
          >
            <RefreshCw size={11} /> Retry
          </button>
        </div>
      </div>
    </div>
  );
};

export default BarcodeScanner;
